import { getUserIdWithoutPrefix } from 'utils/commons';
import {
  PayrollCustomFieldDefinition,
  PayrollFieldClause,
} from 'api/gql-utils/employeeDataSettings/types';
import {
  CustomFieldEnumOption,
  CustomFieldDateRangeValue,
  CustomFieldFilterValue,
  CustomFieldSelectedFilter,
  FilterableCustomFieldDefinition,
} from './types';
import { DEFAULT_FIELD_SECTION } from './constants';

const FIELD_KEY_PATTERN = /^(?!.*\.\.)(?!\.)(?!.*\.$)[a-z0-9_][a-z0-9_.]{0,63}$/;

export const createFilterId = () => `custom-field-filter-${Date.now()}-${Math.random()}`;

export const asFilterableDefinition = (
  definition?: PayrollCustomFieldDefinition | null,
): FilterableCustomFieldDefinition | null => {
  if (!definition?.fieldKey || !definition.label || !definition.valueType) return null;

  return {
    ...definition,
    fieldKey: definition.fieldKey,
    label: definition.label,
    valueType: definition.valueType,
  };
};

export const getFieldSection = (definition: FilterableCustomFieldDefinition): string => {
  return definition.section || definition.groupKey || DEFAULT_FIELD_SECTION;
};

export const getDateRangeValue = (
  value: CustomFieldFilterValue,
): CustomFieldDateRangeValue => {
  if (typeof value !== 'object' || Array.isArray(value) || value === null) {
    return { startDate: null, endDate: null };
  }

  return value;
};

export const parseDate = (value: string | null): Date | null => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

export const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getTextInputPlaceholder = (definition: FilterableCustomFieldDefinition): string => {
  switch (definition.valueType) {
    case 'INTEGER':
    case 'DECIMAL':
    case 'CURRENCY':
      return 'Enter number';
    default:
      return 'Enter value';
  }
};

const isBooleanValueType = (valueType?: string | null): boolean => {
  return valueType === 'BOOLEAN' || valueType === 'BOOL';
};

export const getFilterableDefinitions = (
  definitions: Array<FilterableCustomFieldDefinition | null | undefined>,
): FilterableCustomFieldDefinition[] =>
  definitions
    .filter((definition): definition is FilterableCustomFieldDefinition => {
      return Boolean(definition?.fieldKey && definition?.label && definition?.valueType);
    })
    .sort((a, b) => {
      const orderA = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      return a.label.localeCompare(b.label);
    });

export const getEnumOptions = (
  definition?: FilterableCustomFieldDefinition,
): CustomFieldEnumOption[] => {
  if (isBooleanValueType(definition?.valueType)) {
    return [
      { value: 'true', title: 'True' },
      { value: 'false', title: 'False' },
    ];
  }

  const enumValues = definition?.enumValues;

  if (Array.isArray(enumValues)) {
    return enumValues.map((value) => ({ value, title: value }));
  }

  if (enumValues && typeof enumValues === 'object') {
    return Object.entries(enumValues)
      .filter(([, option]) => !option?.deprecated)
      .map(([value, option]) => ({
        value,
        title: option?.label || value,
      }));
  }

  return [];
};

export const isSelectableDefinition = (definition?: FilterableCustomFieldDefinition): boolean => {
  return definition?.valueType === 'ENUM' || isBooleanValueType(definition?.valueType);
};

export const isDateDefinition = (definition?: FilterableCustomFieldDefinition): boolean => {
  return definition?.valueType === 'DATE' || definition?.valueType === 'DATETIME';
};

export const isNumericDefinition = (definition?: FilterableCustomFieldDefinition): boolean => {
  return ['INTEGER', 'DECIMAL', 'CURRENCY'].includes(String(definition?.valueType));
};

export const isSupportedFilterDefinition = (
  definition?: FilterableCustomFieldDefinition,
): boolean => {
  return Boolean(definition?.valueType && definition.valueType !== 'FILE');
};

const formatSelectedValue = (
  value: string,
  definition: FilterableCustomFieldDefinition,
): string => {
  if (isBooleanValueType(definition.valueType)) {
    return value === 'true' ? 'true' : 'false';
  }

  return value;
};

const isDateRangeValue = (value: CustomFieldFilterValue): value is CustomFieldDateRangeValue => {
  return typeof value === 'object' && !Array.isArray(value) && value !== null;
};

export const hasFilterValue = (value: CustomFieldFilterValue): boolean => {
  if (Array.isArray(value)) return value.filter(Boolean).length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  if (isDateRangeValue(value)) return Boolean(value.startDate || value.endDate);
  return false;
};

const isValidDateValue = (value: string): boolean => {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
};

const buildDateClauses = (
  fieldKey: string,
  value: CustomFieldDateRangeValue,
  label: string,
): PayrollFieldClause[] => {
  if (!value.startDate && !value.endDate) {
    throw new Error(`Enter a value for ${label}`);
  }

  if (
    (value.startDate && !isValidDateValue(value.startDate)) ||
    (value.endDate && !isValidDateValue(value.endDate))
  ) {
    throw new Error(`Enter ${label} in YYYY-MM-DD format`);
  }

  const clauses: PayrollFieldClause[] = [];
  if (value.startDate) {
    clauses.push({ fieldKey, op: 'GTE', values: [value.startDate] });
  }
  if (value.endDate) {
    clauses.push({ fieldKey, op: 'LTE', values: [value.endDate] });
  }
  return clauses;
};

const buildTextClause = (
  fieldKey: string,
  value: string,
  definition: FilterableCustomFieldDefinition,
): PayrollFieldClause => {
  const normalizedValue = value.trim();
  if (!normalizedValue) {
    throw new Error(`Enter a value for ${definition.label}`);
  }

  if (isNumericDefinition(definition)) {
    const numericValue = Number(normalizedValue);
    if (Number.isNaN(numericValue)) {
      throw new Error(`Enter a valid number for ${definition.label}`);
    }
    return { fieldKey, op: 'EQ', values: [String(numericValue)] };
  }

  return { fieldKey, op: 'ILIKE', values: [`%${normalizedValue}%`] };
};

export const buildCustomFieldClauses = (
  selectedFilters: CustomFieldSelectedFilter[],
  definitions: FilterableCustomFieldDefinition[],
): PayrollFieldClause[] => {
  const definitionByKey = new Map(definitions.map((definition) => [definition.fieldKey, definition]));

  return selectedFilters.flatMap((filter) => {
    const definition = definitionByKey.get(filter.fieldKey);
    if (!definition || !FIELD_KEY_PATTERN.test(filter.fieldKey)) {
      throw new Error('Invalid custom field selected');
    }

    if (isDateRangeValue(filter.value)) {
      return buildDateClauses(filter.fieldKey, filter.value, definition.label);
    }

    if (typeof filter.value === 'string') {
      return [buildTextClause(filter.fieldKey, filter.value, definition)];
    }

    const values = filter.value.filter(Boolean);
    if (values.length === 0) {
      throw new Error(`Enter a value for ${definition.label}`);
    }

    const selectedValues = values.map((value) => formatSelectedValue(value, definition));
    if (selectedValues.length === 1) {
      return [{ fieldKey: filter.fieldKey, op: 'EQ' as const, values: selectedValues }];
    }

    return [{ fieldKey: filter.fieldKey, op: 'IN' as const, values: selectedValues }];
  });
};

export const getInitialFilterValue = (
  definition: FilterableCustomFieldDefinition,
): CustomFieldFilterValue => {
  if (isSelectableDefinition(definition)) return [];
  if (isDateDefinition(definition)) return { startDate: null, endDate: null };
  return '';
};

export const getInitialFiltersFromSavedView = (
  fieldKeys: string[] | undefined | null,
  definitions: FilterableCustomFieldDefinition[],
): CustomFieldSelectedFilter[] => {
  if (!fieldKeys?.length) return [];
  const definitionByKey = new Map(definitions.map((d) => [d.fieldKey, d]));
  return fieldKeys.flatMap((fieldKey) => {
    const definition = definitionByKey.get(fieldKey);
    if (!definition) return [];
    return [
      {
        id: `saved-view-${fieldKey}`,
        fieldKey,
        value: getInitialFilterValue(definition),
      },
    ];
  });
};

export const extractPeopleIdsFromUserIds = (userIds: string[]): number[] => {
  return getUserIdWithoutPrefix(userIds)
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0);
};
